<script setup>
import { useGenerateImageVariant } from '@core/composable/useGenerateImageVariant'
import { VNodeRenderer } from '@layouts/components/VNodeRenderer'
import { themeConfig } from '@themeConfig'
import authV2ForgotPasswordIllustrationDark from '@images/pages/auth-v2-forgot-password-illustration-dark.png'
import authV2ForgotPasswordIllustrationLight from '@images/pages/auth-v2-forgot-password-illustration-light.png'
import authV2MaskDark from '@images/pages/misc-mask-dark.png'
import authV2MaskLight from '@images/pages/misc-mask-light.png'
import { onMounted, ref, computed, onUnmounted } from 'vue'
import { useGlobalVariablesStore } from '@/composables/useGlobalStore'
import UsersController from '@/Controller/users'
import { useToast } from 'vue-toastification'
import { getCurrentInstance } from 'vue'
import { useRouter } from 'vue-router'

const otp = ref('')
const authThemeImg = useGenerateImageVariant(authV2ForgotPasswordIllustrationLight, authV2ForgotPasswordIllustrationDark)
const authThemeMask = useGenerateImageVariant(authV2MaskLight, authV2MaskDark)
const globalVariables=useGlobalVariablesStore()
const mobile = globalVariables.mobileToVerify
const toast=useToast()
const { proxy } = getCurrentInstance()
const router = useRouter()
const sendingSms=ref(false)

definePage({
  meta: {
    layout: 'blank',
    unauthenticatedOnly: true,
  },
})

const isOtpInserted = ref(false)
const timer=ref(120)

const onFinish = async() => {
  var validateResult
  try{
    loading.value=true
    validateResult= await UsersController.validateMobile(mobile, otp.value)
  }catch(ex){
    toast.error(proxy.$t('Code is not valid'))
  }finally{
    isOtpInserted.value = validateResult?.mobileValidationCode==otp.value
    console.log('isOtpInserted', isOtpInserted.value)
    loading.value=false
    if(isOtpInserted.value){
      toast.success(proxy.$t('Code is valid'))
      router.push('/change-password')
    }
  }
  
}

onMounted(()=>{
  startTimer()
})


const totalTime = 2 * 60 // 2 minutes in seconds
const timeRemaining = ref(totalTime)
const isRunning = ref(false)
let timerInterval = null

// Computed properties
const minutes = computed(() => Math.floor(timeRemaining.value / 60))
const seconds = computed(() => timeRemaining.value % 60)

const formattedTime = computed(() => {
  return `${minutes.value.toString().padStart(2, '0')}:${seconds.value.toString().padStart(2, '0')}`
})

// Timer methods
const startTimer = () => {
  if (timeRemaining.value <= 0) return
  
  isRunning.value = true
  timerInterval = setInterval(() => {
    timeRemaining.value--
    
    if (timeRemaining.value <= 0) {
      stopTimer()
      timeRemaining.value = 0
    }
  }, 1000)
}

const stopTimer = () => {
  isRunning.value = false
  if (timerInterval) {
    clearInterval(timerInterval)
    timerInterval = null
  }
}

const resetTimer = () => {
  stopTimer()
  timeRemaining.value = totalTime
}

const restartTimer = ()=>{
  resetTimer()
  startTimer()
}

const loading=ref(false)
const validate=async()=>{

}

const sendSms=async()=>{
  sendingSms.value=true
  var setValidateResult=await UsersController.setValidationCode(globalVariables.mobileToVerify)
  sendingSms.value=false
  if(setValidateResult==globalVariables.mobileToVerify){
    restartTimer()
  }
}

// Cleanup on component unmount
onUnmounted(() => {
  stopTimer()
})
</script>

<template>
  <RouterLink to="/">
    <div class="auth-logo d-flex align-center gap-x-3">
      <VNodeRenderer :nodes="themeConfig.app.logo" />
      <h1 class="auth-title">
        {{ $t(themeConfig.app.title) }}
      </h1>
    </div>
  </RouterLink>

  <VRow
    class="auth-wrapper bg-surface"
    no-gutters
  >
    <VCol
      cols="12"
      md="8"
      class="d-flex align-center justify-center"
    >
      <VCard
        flat
        :max-width="500"
        class="mt-12 mt-sm-0 pa-4"
      >
        <VCardText>
          <h4 class="text-h4 mb-1 text-center">
          {{$t('Password verification')}}
          </h4>
          <p class="mb-0 mt-3 text-center">
            {{$t("Enter the code sent by SMS")}}
          </p>
        </VCardText>

        <VCardText>
          <VForm @submit.prevent="() => {}">
            <VRow>
              <!-- phone -->
              <VCol cols="12">
                <div class="ltr-otp-container">
                  <VOtpInput
                    v-model="otp"
                    :disabled="isOtpInserted"
                    type="number"
                    class="pa-0 ltr-otp" 
                    @finish="onFinish"
                    autofocus
                  />
                </div>
              </VCol>

              <VCol class="text-center">
                {{formattedTime}}
              </VCol>

              <!-- Reset link -->
              <VCol cols="12">
                <VBtn
                  block
                  class="rounded-lg"
                  :color="!isOtpInserted ? 'secondary':'primary'"
                  :disabled="!isOtpInserted"
                  @click="validate"
                  :loading="loading"
                >
                  {{ $t('Confirm and continue') }}
                </VBtn>
              </VCol>

              <!-- back to login -->
              <VCol cols="12">
                <div class="d-flex align-center flex-wrap justify-space-between">
                  <RouterLink
                    class="d-flex"
                    :to="{ name: 'forgot-password' }"
                  >
                    <VIcon
                      icon="tabler-transfer"
                      size="20"
                      class="me-1 flip-in-rtl"
                    />
                    <span>{{$t('Change mobile number')}}</span>
                  </RouterLink>

                  <a class="cursor-pointer" v-show="timeRemaining<=0 && !sendingSms" @click="sendSms"><span>{{$t('Resend')}}</span></a>
                </div>
              </VCol>
            </VRow>
          </VForm>
        </VCardText>
      </VCard>
    </VCol>

    <VCol
      md="4"
      class="d-none d-md-flex"
    >
      <div class="position-relative bg-background w-100 me-0">
        <div
          class="d-flex align-center justify-center w-100 h-100"
        >
          <VImg
            max-width="468"
            :src="authThemeImg"
            class="auth-illustration mt-16 mb-2"
          />
        </div>

        <img
          class="auth-footer-mask"
          :src="authThemeMask"
          alt="auth-footer-mask"
          height="280"
          width="100"
        >
      </div>
    </VCol>
  </VRow>
</template>

<style lang="scss">
@use "@core/scss/template/pages/page-auth.scss";
</style>

<style scoped>
.ltr-otp-container {
  direction: ltr;
}

.ltr-otp {
  direction: ltr;
  text-align: left;
}
</style>
